
import React, { useState, useEffect } from 'react';
import { AnalysisResult } from './hooks/useProjectAnalysis';
import { CheckCircle, AlertTriangle, XCircle, ChevronRight, X, Bot } from 'lucide-react';

interface AiAssistantProps {
  analysis: AnalysisResult;
  onNavigateToFix: (field: string) => void;
}

// Blendet die Kinder erst nach der Verzögerung ein. Wird nur gemountet, solange
// die Anzeige zulässig ist – dadurch startet die Wartezeit bei jeder neuen Chance von vorn.
const DelayedReveal: React.FC<{ delayMs: number; restartKey: unknown; children: React.ReactNode }> = ({ delayMs, restartKey, children }) => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), delayMs);
    return () => clearTimeout(timer);
  }, [delayMs, restartKey]);

  if (!visible) return null;
  return <>{children}</>;
};

export const AiAssistant: React.FC<AiAssistantProps> = ({ analysis, onNavigateToFix }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [hasUserStarted, setHasUserStarted] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  // Latch: sobald zum ersten Mal etwas erfasst wurde, bleibt der Assistent aktiv.
  if (analysis.score > 0 && !hasUserStarted) {
    setHasUserStarted(true);
  }

  const mascotEligible = hasUserStarted && analysis.status === 'critical' && !isOpen;

  const getStatusColor = () => {
    switch (analysis.status) {
      case 'critical': return 'bg-red-500 shadow-red-200 border-red-100';
      case 'warning': return 'bg-amber-400 shadow-amber-200 border-amber-100';
      case 'good': return 'bg-emerald-500 shadow-emerald-200 border-emerald-100';
    }
  };

  const getIcon = () => {
    switch (analysis.status) {
      case 'critical': return <XCircle className="w-6 h-6 text-white" />;
      case 'warning': return <AlertTriangle className="w-6 h-6 text-white" />;
      case 'good': return <CheckCircle className="w-6 h-6 text-white" />;
    }
  };

  return (
    <>
    <style>{`
      @keyframes float {
        0%, 100% { transform: translateY(0px); }
        50% { transform: translateY(-8px); }
      }
      @keyframes blink {
        0%, 90%, 100% { transform: scaleY(1); }
        95% { transform: scaleY(0.1); }
      }
      @keyframes wave {
        0% { transform: rotate(0deg); }
        25% { transform: rotate(-20deg); }
        50% { transform: rotate(10deg); }
        75% { transform: rotate(-10deg); }
        100% { transform: rotate(0deg); }
      }
      @keyframes popIn {
        0% { opacity: 0; transform: scale(0) translateY(20px); }
        60% { opacity: 1; transform: scale(1.1) translateY(-10px); }
        100% { opacity: 1; transform: scale(1) translateY(0); }
      }
      
      .mascot-float { animation: float 3s ease-in-out infinite; }
      .mascot-blink { animation: blink 4s infinite; }
      .mascot-wave { animation: wave 2s ease-in-out infinite; transform-origin: bottom right; }
      .mascot-enter { animation: popIn 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) forwards; }
    `}</style>

    <div className="fixed bottom-6 right-6 z-40 flex flex-col items-end pointer-events-none">
      
      {isOpen && (
        <div className="mb-4 bg-white rounded-2xl shadow-2xl border border-slate-100 w-80 overflow-hidden pointer-events-auto animate-in slide-in-from-bottom-5 fade-in duration-300 relative z-50">
           <div className={`p-4 ${analysis.status === 'critical' ? 'bg-red-50' : analysis.status === 'warning' ? 'bg-amber-50' : 'bg-emerald-50'} border-b border-black/5 flex justify-between items-start`}>
              <div className="flex gap-3">
                  <div className="bg-white/50 p-1.5 rounded-lg border border-black/5">
                    <Bot className="w-5 h-5 text-slate-700" />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-800 text-sm leading-tight">
                        Projekt-Analyse
                    </h3>
                    <div className="text-[11px] font-medium opacity-80 mt-0.5 uppercase tracking-wider">
                        {analysis.status === 'critical' ? 'Unvollständig' : analysis.status === 'warning' ? 'Verbesserungswürdig' : 'Vollständig'}
                    </div>
                  </div>
              </div>
              <button onClick={() => setIsOpen(false)} className="p-1 hover:bg-black/10 rounded-full transition-colors"><X className="w-4 h-4" /></button>
           </div>
           
           <div className="p-5 max-h-[60vh] overflow-y-auto">
               <div className="mb-6">
                  <div className="flex justify-between text-xs font-bold text-slate-500 mb-1">
                      <span>Fortschritt</span>
                      <span>{analysis.score}%</span>
                  </div>
                  <div className="h-2.5 w-full bg-slate-100 rounded-full overflow-hidden border border-slate-200/50">
                      <div 
                        className={`h-full transition-all duration-1000 ${analysis.status === 'critical' ? 'bg-red-500' : analysis.status === 'warning' ? 'bg-amber-400' : 'bg-emerald-500'}`} 
                        style={{ width: `${analysis.score}%` }}
                      ></div>
                  </div>
               </div>

               {analysis.missingFields.length > 0 && (
                   <div className="mb-4">
                       <h4 className="text-[11px] font-extrabold text-red-500 uppercase tracking-widest mb-2 flex items-center gap-1">
                           <AlertTriangle className="w-3 h-3" /> Kritische Lücken
                       </h4>
                       <ul className="space-y-2">
                           {analysis.missingFields.map(field => (
                               <li key={field} className="flex justify-between items-center text-sm bg-red-50 p-2.5 rounded-lg border border-red-100 group hover:border-red-200 transition-colors">
                                   <span className="text-slate-700 font-bold text-xs">{field}</span>
                                   <button 
                                     onClick={() => { onNavigateToFix(field); setIsOpen(false); }}
                                     className="text-[11px] font-bold bg-white border border-red-200 text-red-600 px-2 py-1 rounded shadow-sm hover:bg-red-600 hover:text-white transition-all flex items-center"
                                   >
                                      Beheben <ChevronRight className="w-3 h-3 ml-0.5" />
                                   </button>
                               </li>
                           ))}
                       </ul>
                   </div>
               )}

               {analysis.suggestions.length > 0 && (
                   <div>
                       <h4 className="text-[11px] font-extrabold text-amber-500 uppercase tracking-widest mb-2">Optimierungen</h4>
                       <ul className="space-y-2">
                           {analysis.suggestions.map(sug => (
                               <li key={sug} className="flex justify-between items-center text-xs text-slate-600 p-2 rounded-lg border border-slate-100 bg-slate-50/50">
                                   <span>{sug}</span>
                                    <button 
                                     onClick={() => { onNavigateToFix(sug); setIsOpen(false); }}
                                     className="text-[11px] font-bold text-slate-400 hover:text-blue-600 px-1.5 py-0.5 transition-colors whitespace-nowrap"
                                   >
                                      Zeigen
                                   </button>
                               </li>
                           ))}
                       </ul>
                   </div>
               )}

               {analysis.status === 'good' && (
                   <div className="text-center py-4">
                       <div className="inline-block p-3 bg-emerald-100 rounded-full mb-3 animate-bounce">
                           <CheckCircle className="w-8 h-8 text-emerald-600" />
                       </div>
                       <p className="text-sm font-bold text-slate-800">Alles perfekt!</p>
                       <p className="text-xs text-slate-500 mt-1">Die Unterlage ist bereit für den Export.</p>
                   </div>
               )}
           </div>
        </div>
      )}

      {mascotEligible && (
       <DelayedReveal delayMs={4000} restartKey={analysis.score}>
         <div
            onClick={() => setIsOpen(true)}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            className="mb-3 mr-0 relative mascot-enter pointer-events-auto cursor-pointer group flex flex-col items-end"
         >
             <div className="bg-white text-slate-800 text-xs font-bold px-4 py-3 rounded-2xl rounded-tr-sm shadow-xl border border-slate-100 max-w-[200px] mb-2 transform group-hover:-translate-y-1 transition-transform relative">
                <div className="absolute -bottom-2 right-6 w-4 h-4 bg-white border-b border-r border-slate-100 transform rotate-45"></div>
                <span className="text-red-500 mr-1">⚠️</span> Psst! Hier fehlt noch was Wichtiges.
             </div>

             <div className="relative w-16 h-16 mr-2">
                 <div className="w-full h-full mascot-float relative">
                    <div className="absolute top-4 left-2 w-12 h-10 bg-slate-800 rounded-xl shadow-lg border-2 border-white flex flex-col items-center justify-center z-20">
                        <div className="flex gap-2 mascot-blink">
                             <div className={`w-2.5 h-2.5 rounded-full ${analysis.status === 'critical' ? 'bg-red-400 shadow-[0_0_5px_rgba(248,113,113,0.8)]' : 'bg-blue-400'}`}></div>
                             <div className={`w-2.5 h-2.5 rounded-full ${analysis.status === 'critical' ? 'bg-red-400 shadow-[0_0_5px_rgba(248,113,113,0.8)]' : 'bg-blue-400'}`}></div>
                        </div>
                    </div>
                    <div className="absolute top-1 left-1 w-14 h-7 bg-yellow-400 rounded-t-full border-2 border-white shadow-sm flex items-center justify-center z-30">
                        <div className="w-5 h-0.5 bg-yellow-600/30 rounded-full mt-1"></div>
                    </div>
                    <div className={`absolute top-8 -right-1 w-3.5 h-3.5 bg-white border-2 border-slate-300 rounded-full z-10 origin-bottom-left ${isHovered ? 'mascot-wave' : ''}`}></div>
                    <div className="absolute top-8 -left-1 w-3.5 h-3.5 bg-white border-2 border-slate-300 rounded-full z-10"></div>
                 </div>
             </div>
         </div>
       </DelayedReveal>
      )}

      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`w-14 h-14 rounded-full shadow-xl flex items-center justify-center transition-all hover:scale-110 active:scale-95 pointer-events-auto border-4 border-white relative z-30 ${getStatusColor()}`}
      >
        {getIcon()}
        {analysis.status === 'critical' && !isOpen && (
            <span className="absolute top-0 right-0 flex h-3 w-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500 border border-white"></span>
            </span>
        )}
      </button>

    </div>
    </>
  );
};
